import React, { useEffect, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { useSelector } from 'hooks/redux'
import { isEmpty, complement, any } from 'ramda'
import { classNames } from 'utils/utils'
import { isFunction } from 'utils/types'
import {
  NEW,
  RETURNED,
  ACTIVE,
  COMPLEX,
  TIMER,
  OBSOLETE,
} from 'utils/constants'
import strings from 'utils/strings.js'
import { Header, Paragraph } from 'components/patterns/Typography'

import featureMetaSchema from 'schemas/validation/feature/featureMetaSchema'
import { schemaProp } from 'schemas/validation'

import { getIsEditing, getIsSimulating } from 'selectors/navigator'
import { getOrphanDescriptions } from 'selectors/feature/logicNodes'
import { getOrphanOutputDescriptions } from 'selectors/feature/outputs'
import { getInErrorState } from 'selectors/validation'
import { getFeatureHarnessIds } from 'selectors/feature/harnesses'
import { getIsSchoolBusFeatureSubType } from 'selectors/feature/featureMetadata'
import { getCanManageFastFeatures } from 'selectors/authorization/checPlus'

import { setSelectedIdWithDrawer, setIsEditing } from 'actions/navigator'
import { loadFastFeatureStatus } from 'actions/feature/fastFeatures'
import { useMakeDispatchCallback } from 'hooks'

import { DrawerDivider, Button } from 'components/patterns'
import { TextInput } from 'components/patterns/Inputs'
import Editor from 'components/feature/Editor'
import SimpleList from 'components/SimpleList'
import {
  HeaderStyled,
  StyledHarnessBtnWrapper,
} from 'components/feature/NavigatorPanes/editors/Editors.style'
import { useFeatureRevision } from 'components/feature/FeatureRevision/useFeatureRevision'
import ModuleNameRadioEditor from './ModuleNameRadioEditor'
import FeatureEdit from './FeatureEdit'
import FeatureReadOnly from './FeatureReadOnly'
import withStyle, {
  StyledDescriptionInput,
  FeatureEditorStyledContainer,
  EditorInnerStyledContainer,
} from './FeatureEditor.style'

import FeatureStatus from './FeatureStatus'
import { ViewNotesButton } from '../NavigatorPanes/FeatureNotes/FeatureNotes'
import PowerNetLevelSimulator from '../Simulation/PowerNetLevelSimulator'
import FastFeatureControlContainer from '../FastFeatureControl'

const rule = schemaProp(featureMetaSchema)

const FeatureEditor = (props) => {
  const {
    description,
    status,
    moduleName,
    functionLinkScraps,
    moduleNameIsEditable,
    updateModuleName,
    updateDescription,
    className,
    resetFeature,
    featureId,
    allDescriptions,
    setStatus,
    resetHarnesses,
    renderHarnessBtn,
  } = props

  const isInitial = featureId === 'initial'

  const dispatch = useDispatch()

  const setSelectedIdCallback = useMakeDispatchCallback(setSelectedIdWithDrawer)

  const isEditing = useSelector(getIsEditing)
  const isSchoolBusFeature = useSelector(getIsSchoolBusFeatureSubType)
  const isSimulating = useSelector(getIsSimulating)
  const canManageFastFeatures = useSelector(getCanManageFastFeatures)

  // Reset fast feature Redux state whenever we navigate to a different Active feature
  useEffect(() => {
    if (status === ACTIVE && featureId && featureId !== 'initial') {
      dispatch(loadFastFeatureStatus({ featureId }))
    }
  }, [featureId, status, dispatch])

  const [confirmActivate, setConfirmActivate] = useState(false)
  const [confirmObsolete, setConfirmObsolete] = useState(false)
  const [ecuToChange, setEcuToChange] = useState(null)

  const orphanLogicNodeDescriptions = useSelector(getOrphanDescriptions)
  const outputOrphanDescriptions = useSelector(getOrphanOutputDescriptions)

  const activateExplanation = useMemo(() => {
    const notIsEmpty = complement(isEmpty)
    const showChangeLists = any(notIsEmpty, [
      orphanLogicNodeDescriptions[COMPLEX],
      orphanLogicNodeDescriptions[TIMER],
      outputOrphanDescriptions,
    ])

    return (
      <div>
        <Paragraph>{strings.confirm_activate_feature_body}</Paragraph>
        {showChangeLists && (
          <>
            <Paragraph>{strings.activate_remove_unused_items}</Paragraph>
            <SimpleList
              heading={<b>{strings.outputs}</b>}
              collection={outputOrphanDescriptions}
            />
            <SimpleList
              heading={<b>{strings.logic_sets}</b>}
              collection={orphanLogicNodeDescriptions[COMPLEX]}
            />
            <SimpleList
              heading={<b>{strings.timers_title}</b>}
              collection={orphanLogicNodeDescriptions[TIMER]}
            />
          </>
        )}
      </div>
    )
  }, [orphanLogicNodeDescriptions, outputOrphanDescriptions])

  const isReturned = status === RETURNED
  const inDesign = status === NEW || isReturned
  const featureIsEditable = isEditing && inDesign
  const revision = useFeatureRevision(featureId)
  const isRevision = revision !== '-'
  const inErrorState = useSelector(getInErrorState)
  useEffect(() => {
    if (inErrorState && !isEditing) dispatch(setIsEditing(true))
  }, [inErrorState, isEditing, dispatch])

  // harnesses logic
  const harnesses = useSelector(getFeatureHarnessIds)

  const harnessesMapped = useMemo(
    () => harnesses.map(strings.harness_display_in_list),
    [harnesses]
  )

  const customOverlayContent = {
    heading: strings.change_ecu_heading(ecuToChange),
    subheading: strings.change_ecu_subheading,
    lists: [
      {
        heading: strings.harnesses,
        items: harnessesMapped,
      },
    ],
  }

  const changeEcuActionButtons = [
    {
      text: strings.keep_ecu_and_harnesses,
      type: 'primary',
      action: () => setEcuToChange(null),
    },
    {
      text: strings.change_ecu_and_harnesses,
      type: 'secondary',
      action: () => {
        setEcuToChange(null)
        resetHarnesses()
        updateModuleName(ecuToChange, featureId)
      },
    },
  ]

  const isFullHeightEditor = confirmActivate || confirmObsolete

  return (
    <FeatureEditorStyledContainer
      style={{
        marginLeft: '-20px',
        height: isFullHeightEditor ? '100%' : 'auto',
      }}
    >
      {isFunction(renderHarnessBtn) && (
        <StyledHarnessBtnWrapper>{renderHarnessBtn()}</StyledHarnessBtnWrapper>
      )}
      <Editor
        className={classNames(className, 'feature-editor')}
        clearItem={() => resetFeature(featureId)}
        clearHeaderText={strings.clear_item_heading(description)}
        clearExplanation={strings.clear_feature_explanation}
        showDelete={false}
        showClear={isEditing}
        onCancel={() => {
          setConfirmActivate(false)
          setConfirmObsolete(false)
        }}
        activateAction={() => setStatus(ACTIVE)}
        obsoleteAction={() => setStatus(OBSOLETE)}
        showActivateAction={confirmActivate}
        showObsoleteAction={confirmObsolete}
        activateHeaderText={strings.confirm_activate_feature_header}
        obsoleteHeaderText={strings.obsolete_feature_title}
        obsoleteExplanation={strings.confirm_obsolete_feature_body}
        activateExplanation={activateExplanation}
        isInitial={isInitial}
        customOverlayContent={customOverlayContent}
        showCustomOverlay={ecuToChange !== null}
        customActionButtons={changeEcuActionButtons}
      >
        <ViewNotesButton id={featureId} />
        <EditorInnerStyledContainer>
          <HeaderStyled>
            <Header type="h3">{description}</Header>
          </HeaderStyled>
          {featureIsEditable && !isRevision && (
            <StyledDescriptionInput>
              <TextInput
                rules={rule('description', {
                  descriptions: allDescriptions,
                })}
                label={strings.feature_description}
                name={strings.feature_description}
                placeholder={strings.feature_description}
                autoFocus={!isRevision}
                onChange={(event) => updateDescription(event, featureId)}
                value={description}
                disabled={isRevision}
              />
            </StyledDescriptionInput>
          )}
          {!isInitial && (
            <>
              <FeatureStatus
                setConfirmActivate={setConfirmActivate}
                setConfirmObsolete={setConfirmObsolete}
                isEditing={isEditing}
              />
              <DrawerDivider />
              {isSimulating && <PowerNetLevelSimulator />}
            </>
          )}
          {featureIsEditable && (
            <>
              {/* TODO: add back with additional logic as per Task 53017 outcome */}
              {/* <StyledBusSwitch
              className="bus-type-switch"
              value={isSchoolBusFeature}
              onChange={onBusModeChange}
              name={strings.school_bus_mode}
              label={strings.school_bus_mode}
              rules={yup.boolean().nullable()}
              switchLabels={switchLabel}
              disabled={isReturned}
            />
            <SchoolBusMessage>
              <InfoIcon icon="alert_info_i_round_solid" fill="blackSecondary" />
              <Paragraph size="small">
                {strings.school_bus_valid_modules_message}
              </Paragraph>
            </SchoolBusMessage> */}
              <ModuleNameRadioEditor
                className="feature-edit-module-selector"
                moduleNameIsEditable={
                  (moduleNameIsEditable || isInitial) && !isRevision
                }
                moduleName={moduleName}
                isInitial={isInitial}
                featureId={featureId}
                busMode={isSchoolBusFeature}
                harnessesMapped={harnessesMapped}
              />
            </>
          )}
          {featureIsEditable && !isRevision ? (
            <FeatureEdit {...props} isRevision={isRevision} />
          ) : (
            <FeatureReadOnly {...props} />
          )}
          {!isInitial && (
            <>
              <DrawerDivider />
              <Paragraph weight="bold" size="small">
                {strings.function}
              </Paragraph>
              {functionLinkScraps.map(({ uri, description }) => (
                <Paragraph key={uri}>
                  <Button
                    key={uri}
                    size="small"
                    link
                    onClick={() => setSelectedIdCallback(uri)}
                  >
                    {description}
                  </Button>
                </Paragraph>
              ))}
            </>
          )}
          {!isInitial && status === ACTIVE && canManageFastFeatures && (
            <>
              <DrawerDivider />
              <FastFeatureControlContainer />
            </>
          )}
        </EditorInnerStyledContainer>
      </Editor>
    </FeatureEditorStyledContainer>
  )
}

FeatureEditor.defaultProps = {
  functionLinkScraps: [],
}

FeatureEditor.propTypes = {
  partNumber: PropTypes.string,
  status: PropTypes.string,
  moduleName: PropTypes.string,
  masterNumber: PropTypes.string,
  releaseNumber: PropTypes.string,
  subGroup: PropTypes.string,
  description: PropTypes.string,
  priority: PropTypes.number,
  successorCode: PropTypes.string,
  functionLinkScraps: PropTypes.array,
  updateDescription: PropTypes.func,
  updateReleaseNumber: PropTypes.func,
  updateSubGroup: PropTypes.func,
  updateSuccessorCode: PropTypes.func,
  updatePriority: PropTypes.func,
  className: PropTypes.string,
  isSameOrg: PropTypes.bool,
  updateModuleName: PropTypes.func,
  moduleNameIsEditable: PropTypes.bool,
  id: PropTypes.string,
  type: PropTypes.string,
  updateFeatureType: PropTypes.func,
  subGroups: PropTypes.object,
  isInternalEngineerOrAdmin: PropTypes.bool,
  isInternalFeature: PropTypes.bool,
  resetFeature: PropTypes.func,
  featureId: PropTypes.string,
  allDescriptions: PropTypes.arrayOf(PropTypes.string),
  setStatus: PropTypes.func,
  resetHarnesses: PropTypes.func,
  setBusTypeFeature: PropTypes.func,
  renderHarnessBtn: PropTypes.func,
}

export default withStyle(FeatureEditor)
